import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { getApiV1Url } from "./apiConfig";
import { logout } from "../features/auth/authSlice";

const API_URL = getApiV1Url();

// Custom base query with error handling
const baseQueryWithErrorHandling = fetchBaseQuery({
  baseUrl: API_URL,
  prepareHeaders: (headers, { getState }) => {
    const token = getState().auth.token;
    if (token) headers.set("authorization", `Bearer ${token}`);
    // Disable caching for fresh data
    headers.set("Cache-Control", "no-cache, no-store, must-revalidate");
    headers.set("Pragma", "no-cache");
    headers.set("Expires", "0");
    return headers;
  },
});

// Wrapper to handle CORS and authentication errors
const baseQueryWithReauth = async (args, api, extraOptions) => {
  let result = await baseQueryWithErrorHandling(args, api, extraOptions);
  
  // Handle CORS errors or network failures
  if (result.error) {
    const error = result.error;
    
    // CORS error or network failure
    if (error.status === "FETCH_ERROR" || !error.status) {
      // Check if it's a CORS error
      if (error.error && error.error.includes("CORS")) {
        alert(
          "❌ Connection Error\n\n" +
          "Unable to connect to the API server. This could be because:\n\n" +
          "1. The backend server is not running\n" +
          "2. The API URL is incorrect\n" +
          "3. CORS is not configured properly\n\n" +
          "Current API URL: " + API_URL + "\n\n" +
          "Please logout and login again, or contact your administrator."
        );
      }
    }
    
    // Handle 401 Unauthorized - force logout
    if (error.status === 401) {
      alert(
        "🔒 Session Expired\n\n" +
        "Your session has expired or your authentication token is invalid.\n\n" +
        "Please login again to continue."
      );
      api.dispatch(logout());
      window.location.href = "/login";
    }
    
    // Handle 403 Forbidden
    if (error.status === 403) {
      alert(
        "⛔ Access Denied\n\n" +
        "You don't have permission to perform this action.\n\n" +
        "Please contact your administrator if you believe this is an error."
      );
    }
  }
  
  return result;
};

export const api = createApi({
  reducerPath: "rohaApi",
  tagTypes: ["Dashboard", "Products", "Stock", "Sales", "Returns", "Expenses", "Customers", "Employees", "CashFlow", "Reports", "Files", "ServiceOrders", "MaterialPurchases"],
  baseQuery: baseQueryWithReauth,
  // Keep data fresh - refetch on focus and reconnect
  refetchOnFocus: true,
  refetchOnReconnect: true,
  // Keep unused data for only 60 seconds
  keepUnusedDataFor: 60,
  endpoints: (builder) => ({
    // Auth
    login: builder.mutation({
      query: (credentials) => ({ url: "/auth/login", method: "POST", body: credentials }),
    }),
    getMe: builder.query({ query: () => "/auth/me" }),
    changePassword: builder.mutation({
      query: (body) => ({ url: "/auth/change-password", method: "POST", body }),
    }),

    // Dashboard
    getDashboard: builder.query({
      query: () => "/dashboard",
      providesTags: ["Dashboard"],
    }),

    // Cash Flow
    getCashFlow: builder.query({
      query: (params = {}) => ({ url: "/cashflow", params }),
      providesTags: ["CashFlow"],
    }),

    // Reports
    getReportData: builder.query({
      query: (params = {}) => ({ url: "/reports", params }),
      providesTags: ["Reports"],
    }),

    // Inventory Summary
    getInventorySummary: builder.query({
      query: (params = {}) => ({ url: "/inventory/summary", params }),
      providesTags: ["Stock"],
    }),

    // Customers
    getCustomers: builder.query({
      query: (params = {}) => ({ url: "/customers", params }),
      providesTags: ["Customers"],
    }),
    createCustomer: builder.mutation({
      query: (body) => ({ url: "/customers", method: "POST", body }),
      invalidatesTags: ["Customers", "Dashboard"],
    }),
    updateCustomer: builder.mutation({
      query: ({ id, ...body }) => ({ url: `/customers/${id}`, method: "PUT", body }),
      invalidatesTags: ["Customers"],
    }),
    deleteCustomer: builder.mutation({
      query: (id) => ({ url: `/customers/${id}`, method: "DELETE" }),
      invalidatesTags: ["Customers"],
    }),

    // Employees
    getEmployees: builder.query({
      query: () => "/employees",
      providesTags: ["Employees"],
    }),
    createEmployee: builder.mutation({
      query: (body) => ({ url: "/employees", method: "POST", body }),
      invalidatesTags: ["Employees"],
    }),
    updateEmployee: builder.mutation({
      query: ({ id, ...body }) => ({ url: `/employees/${id}`, method: "PUT", body }),
      invalidatesTags: ["Employees"],
    }),
    deleteEmployee: builder.mutation({
      query: (id) => ({ url: `/employees/${id}`, method: "DELETE" }),
      invalidatesTags: ["Employees"],
    }),
    resetEmployeePassword: builder.mutation({
      query: ({ id, ...body }) => ({ url: `/employees/${id}/reset-password`, method: "POST", body }),
    }),

    // Products
    getProducts: builder.query({
      query: (params = {}) => ({ url: "/products", params }),
      providesTags: ["Products"],
    }),
    createProduct: builder.mutation({
      query: (body) => ({ url: "/products", method: "POST", body }),
      invalidatesTags: ["Products"],
    }),
    updateProduct: builder.mutation({
      query: ({ id, ...body }) => ({ url: `/products/${id}`, method: "PUT", body }),
      invalidatesTags: ["Products"],
    }),
    deleteProduct: builder.mutation({
      query: (id) => ({ url: `/products/${id}`, method: "DELETE" }),
      invalidatesTags: ["Products"],
    }),

    // Stock Items
    getStockItems: builder.query({
      query: (params = {}) => ({ url: "/stock/items", params }),
      providesTags: ["Stock"],
    }),
    createStockItem: builder.mutation({
      query: (body) => ({ url: "/stock/items", method: "POST", body }),
      invalidatesTags: ["Stock", "Dashboard"],
    }),
    updateStockItem: builder.mutation({
      query: ({ id, ...body }) => ({ url: `/stock/items/${id}`, method: "PUT", body }),
      invalidatesTags: ["Stock"],
    }),
    deleteStockItem: builder.mutation({
      query: (id) => ({ url: `/stock/items/${id}`, method: "DELETE" }),
      invalidatesTags: ["Stock"],
    }),

    // Stock Movements
    getStockMovements: builder.query({
      query: (params = {}) => ({ url: "/stock/movements", params }),
      providesTags: ["Stock"],
    }),
    createStockAdjustment: builder.mutation({
      query: (body) => ({ url: "/stock/adjustments", method: "POST", body }),
      invalidatesTags: ["Stock", "Dashboard"],
    }),

    // Stock for POS (available stock)
    getPOSAvailableStock: builder.query({
      query: (params = {}) => ({ url: "/stock/pos-available", params }),
      providesTags: ["Stock"],
    }),

    // Sales
    getSales: builder.query({
      query: (params = {}) => ({ url: "/sales", params }),
      providesTags: ["Sales"],
    }),
    getSale: builder.query({
      query: (id) => `/sales/${id}`,
      providesTags: ["Sales"],
    }),
    createSale: builder.mutation({
      query: (body) => ({ url: "/sales", method: "POST", body }),
      invalidatesTags: ["Sales", "Stock", "Dashboard", "CashFlow"],
    }),

    // Returns
    getReturns: builder.query({
      query: (params = {}) => ({ url: "/returns", params }),
      providesTags: ["Returns"],
    }),
    getReturn: builder.query({
      query: (id) => `/returns/${id}`,
      providesTags: ["Returns"],
    }),
    createReturn: builder.mutation({
      query: (body) => ({ url: "/returns", method: "POST", body }),
      invalidatesTags: ["Returns", "Stock", "Dashboard", "CashFlow"],
    }),

    // Expenses
    getExpenses: builder.query({
      query: (params = {}) => ({ url: "/expenses", params }),
      providesTags: ["Expenses"],
    }),
    createExpense: builder.mutation({
      query: (body) => ({ url: "/expenses", method: "POST", body }),
      invalidatesTags: ["Expenses", "Dashboard", "CashFlow"],
    }),
    updateExpense: builder.mutation({
      query: ({ id, ...body }) => ({ url: `/expenses/${id}`, method: "PUT", body }),
      invalidatesTags: ["Expenses", "CashFlow"],
    }),
    deleteExpense: builder.mutation({
      query: (id) => ({ url: `/expenses/${id}`, method: "DELETE" }),
      invalidatesTags: ["Expenses", "CashFlow"],
    }),

    // File Upload
    uploadFile: builder.mutation({
      query: (formData) => ({ url: "/upload", method: "POST", body: formData }),
      invalidatesTags: ["Files"],
    }),
    getFiles: builder.query({
      query: (params = {}) => ({ url: "/files", params }),
      providesTags: ["Files"],
    }),

    // Service Orders
    getServiceOrders: builder.query({
      query: (params = {}) => ({ url: "/service-orders", params }),
      providesTags: ["ServiceOrders"],
    }),
    getServiceOrder: builder.query({
      query: (id) => `/service-orders/${id}`,
      providesTags: ["ServiceOrders"],
    }),
    createServiceOrder: builder.mutation({
      query: (body) => ({ url: "/service-orders", method: "POST", body }),
      invalidatesTags: ["ServiceOrders", "Dashboard"],
    }),
    updateServiceOrder: builder.mutation({
      query: ({ id, ...body }) => ({ url: `/service-orders/${id}`, method: "PUT", body }),
      invalidatesTags: ["ServiceOrders"],
    }),
    addServiceOrderPayment: builder.mutation({
      query: ({ id, ...body }) => ({ url: `/service-orders/${id}/payments`, method: "POST", body }),
      invalidatesTags: ["ServiceOrders"],
    }),
    deleteServiceOrder: builder.mutation({
      query: (id) => ({ url: `/service-orders/${id}`, method: "DELETE" }),
      invalidatesTags: ["ServiceOrders"],
    }),

    // Material Purchases (Gold & Silver from customers)
    getMaterialPurchases: builder.query({
      query: (params = {}) => ({ url: "/material-purchases", params }),
      providesTags: ["MaterialPurchases"],
    }),
    getMaterialPurchase: builder.query({
      query: (id) => `/material-purchases/${id}`,
      providesTags: ["MaterialPurchases"],
    }),
    createMaterialPurchase: builder.mutation({
      query: (body) => ({ url: "/material-purchases", method: "POST", body }),
      invalidatesTags: ["MaterialPurchases", "Stock", "Dashboard", "CashFlow"],
    }),
    cancelMaterialPurchase: builder.mutation({
      query: (id) => ({ url: `/material-purchases/${id}/cancel`, method: "PUT" }),
      invalidatesTags: ["MaterialPurchases", "Stock"],
    }),
    getMaterialPurchaseStats: builder.query({
      query: (params = {}) => ({ url: "/material-purchases/stats", params }),
      providesTags: ["MaterialPurchases"],
    }),
  }),
});

export const {
  useLoginMutation,
  useGetMeQuery,
  useChangePasswordMutation,
  useGetDashboardQuery,
  useGetCashFlowQuery,
  useGetReportDataQuery,
  useGetInventorySummaryQuery,
  useGetCustomersQuery,
  useCreateCustomerMutation,
  useUpdateCustomerMutation,
  useDeleteCustomerMutation,
  useGetEmployeesQuery,
  useCreateEmployeeMutation,
  useUpdateEmployeeMutation,
  useDeleteEmployeeMutation,
  useResetEmployeePasswordMutation,
  useGetProductsQuery,
  useCreateProductMutation,
  useUpdateProductMutation,
  useDeleteProductMutation,
  useGetStockItemsQuery,
  useCreateStockItemMutation,
  useUpdateStockItemMutation,
  useDeleteStockItemMutation,
  useGetStockMovementsQuery,
  useCreateStockAdjustmentMutation,
  useGetPOSAvailableStockQuery,
  useGetSalesQuery,
  useGetSaleQuery,
  useCreateSaleMutation,
  useGetReturnsQuery,
  useGetReturnQuery,
  useCreateReturnMutation,
  useGetExpensesQuery,
  useCreateExpenseMutation,
  useUpdateExpenseMutation,
  useDeleteExpenseMutation,
  useUploadFileMutation,
  useGetFilesQuery,
  useGetServiceOrdersQuery,
  useGetServiceOrderQuery,
  useCreateServiceOrderMutation,
  useUpdateServiceOrderMutation,
  useAddServiceOrderPaymentMutation,
  useDeleteServiceOrderMutation,
  useGetMaterialPurchasesQuery,
  useGetMaterialPurchaseQuery,
  useCreateMaterialPurchaseMutation,
  useCancelMaterialPurchaseMutation,
  useGetMaterialPurchaseStatsQuery,
} = api;
